/**
 * The view-as session lives in sessionStorage so it belongs to one tab: closing the tab or opening a new one returns
 * the admin to their own identity. The Leela API client reads it on every request without React, so this module stays
 * pure, and a blocked or corrupted store always reads as "not viewing as anyone" rather than failing the request.
 * Once this document has used a live entry, its expiry must not quietly turn the next request into one sent as the
 * admin while the screen still shows the target, so the store remembers, for the life of the document, that the
 * session lapsed and the API client refuses requests until the app restarts.
 */
import { VIEW_AS_MODES, type ViewAsMode } from './view-as-response';

export interface ViewAsEntry {
  readonly target: string;
  readonly mode: ViewAsMode;
  readonly startedAt: number;
  readonly expiresAt: number;
}

export type ViewAsRequestState =
  | { readonly kind: 'none' }
  | { readonly kind: 'active'; readonly entry: ViewAsEntry }
  | { readonly kind: 'lapsed'; readonly hasJustLapsed: boolean };

export const VIEW_AS_HEADER = 'x-leela-view-as';
export const VIEW_AS_TTL_MS = 60 * 60 * 1000;
export const VIEW_AS_STORAGE_KEY = 'leela-view-as';

let hasReadLiveEntry = false;
let hasLapsed = false;

function isViewAsEntry (value: unknown): value is ViewAsEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    'target' in value &&
    typeof value.target === 'string' &&
    value.target !== '' &&
    'mode' in value &&
    VIEW_AS_MODES.has(value.mode) &&
    'startedAt' in value &&
    Number.isFinite(value.startedAt) &&
    'expiresAt' in value &&
    Number.isFinite(value.expiresAt)
  );
}

function parseEntry (raw: string): ViewAsEntry | undefined {
  try {
    const value: unknown = JSON.parse(raw);
    return isViewAsEntry(value) ? value : undefined;
  } catch {
    return undefined;
  }
}

function readRawEntry (): string | null {
  try {
    return window.sessionStorage.getItem(VIEW_AS_STORAGE_KEY);
  } catch {
    return null;
  }
}

function removeEntry (): void {
  try {
    window.sessionStorage.removeItem(VIEW_AS_STORAGE_KEY);
  } catch {
    // Storage blocked by browser privacy settings already reads as no view-as session.
  }
}

function readEntry (): ViewAsEntry | undefined {
  const raw = readRawEntry();
  return raw === null ? undefined : parseEntry(raw);
}

/** Ends the session on purpose, so the next request runs as the signed-in admin. */
export function clearViewAs (): void {
  hasReadLiveEntry = false;
  hasLapsed = false;
  removeEntry();
}

export function setViewAs (entry: ViewAsEntry): void {
  hasLapsed = false;
  try {
    window.sessionStorage.setItem(VIEW_AS_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Without storage the next request carries no header, so the backend echo stays absent and the UI shows the admin.
  }
}

/**
 * Returns the live entry. An expired entry reads as absent; it is removed here only when this document never used it,
 * otherwise it stays for lapseExpiredViewAs so the expiry is handled, not skipped.
 */
export function getViewAsTarget (now: number = Date.now()): ViewAsEntry | undefined {
  if (hasLapsed) {
    return undefined;
  }
  const raw = readRawEntry();
  if (raw === null) {
    return undefined;
  }
  const entry = parseEntry(raw);
  if (entry === undefined) {
    removeEntry();
    return undefined;
  }
  if (entry.expiresAt <= now) {
    if (!hasReadLiveEntry) {
      removeEntry();
    }
    return undefined;
  }
  hasReadLiveEntry = true;
  return entry;
}

/** Removes an expired entry this document used and marks the session lapsed; true only for the call that did so. */
export function lapseExpiredViewAs (now: number = Date.now()): boolean {
  if (hasLapsed || !hasReadLiveEntry) {
    return false;
  }
  const entry = readEntry();
  if (entry === undefined || entry.expiresAt > now) {
    return false;
  }
  removeEntry();
  hasLapsed = true;
  return true;
}

/** What the API client may send: the target, nothing, or a refusal because the session lapsed. */
export function getViewAsRequestState (now: number = Date.now()): ViewAsRequestState {
  if (lapseExpiredViewAs(now)) {
    return { kind: 'lapsed', hasJustLapsed: true };
  }
  if (hasLapsed) {
    return { kind: 'lapsed', hasJustLapsed: false };
  }
  const entry = getViewAsTarget(now);
  return entry === undefined ? { kind: 'none' } : { kind: 'active', entry };
}

const MS_PER_MINUTE = 60_000;

/** Whole minutes left in this tab's session, rounded up, or undefined when no live entry is stored. */
export function getRemainingMinutes (now = Date.now()): number | undefined {
  const entry = getViewAsTarget(now);
  return entry === undefined ? undefined : Math.ceil((entry.expiresAt - now) / MS_PER_MINUTE);
}
